import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { useEffect, useMemo, useState } from 'react';

import { motion } from 'framer-motion';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';

import {
  cn,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger
} from 'src/shared/components/ui';

import OrganizationAvatar from '../../OrganizationAvatar';
import { Typography } from '../../ui';

interface OrganizationMenuProps {
  isOpen: boolean;
  isMobile?: boolean;
}

const OrganizationMenu: React.FC<OrganizationMenuProps> = ({
  isMobile,
  isOpen = true
}) => {
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const user = useAuthStore((state) => state.user);
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );
  const setSelectedOrganization = useAuthStore(
    (state) => state.setSelectedOrganization
  );

  useEffect(() => {
    if (shouldRedirect) {
      window.location.href = '/';
    }
  }, [shouldRedirect]);

  const renderOrganizations = () => {
    if (user?.organizations && user?.organizations?.length > 0) {
      return user?.organizations?.map((org) => (
        <DropdownItem
          onPress={() => {
            setSelectedOrganization(org);
            setShouldRedirect(true);
          }}
          key={org.id}
          classNames={{
            base: [org.id === selectedOrganization?.id && 'bg-gray-4']
          }}
        >
          <div className="flex gap-2 items-center">
            <OrganizationAvatar name={org.name} image={org?.avatar} />
            <Typography variant="body-1" weight="medium">
              {org.name}
            </Typography>
          </div>
        </DropdownItem>
      ));
    }

    return null;
  };

  const organizationName = useMemo(() => {
    if (selectedOrganization) {
      const nameLength = selectedOrganization?.name?.length > 14;
      const truncateTitle = nameLength
        ? selectedOrganization.name.slice(0, 14) + '...'
        : selectedOrganization.name;

      return truncateTitle;
    }

    return null;
  }, [selectedOrganization]);

  return (
    <Dropdown>
      <DropdownTrigger data-testid="organization-menu">
        <div
          className={cn(
            'pb-4 flex gap-2 items-center justify-between hover:opacity-70 transition-opacity cursor-pointer',
            isMobile && 'justify-start'
          )}
        >
          <div className="flex gap-2 items-center">
            <div className="min-w-10">
              <OrganizationAvatar
                name={selectedOrganization?.name}
                image={selectedOrganization?.avatar}
              />
            </div>
            <motion.div
              initial={!isOpen && { maxWidth: 0, opacity: 0 }}
              animate={{
                maxWidth: isOpen ? '100%' : 0,
                opacity: isOpen ? 1 : 0
              }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <Typography variant="body-1" weight="bold" className="leading-4">
                {organizationName}
              </Typography>
            </motion.div>
          </div>
          <ChevronDownIcon className="size-4 text-secondary-text mr-3" />
        </div>
      </DropdownTrigger>
      <DropdownMenu
        data-testid="organization-selector"
        aria-label="Organization List"
        classNames={{ list: 'gap-2 max-h-[80vh] overflow-y-auto' }}
        itemClasses={{ base: 'data-[hover]:bg-gray-4' }}
      >
        {renderOrganizations()}
      </DropdownMenu>
    </Dropdown>
  );
};

export default OrganizationMenu;
